import { useMemo } from "react";

type LoadDraftResult<T> = {
  value: T | null;
  usedPreview: boolean;
  warning: string;
};

type SaveDraftResult = {
  partial: boolean;
  warning: string;
};

type DraftPersistenceOptions<T> = {
  draftKey: string;
  previewKey: string;
  buildPreview: (value: T) => unknown;
};

// sessionStorage JSON 파싱 실패를 흡수하는 안전 파서다.
function parseJson<T>(raw: string | null): T | null {
  if (!raw) return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

/**
 * 편집 draft의 저장/복원/정리 동작을 추상화한다.
 * 용량 초과 시 previewKey만 저장하는 폴백 경로를 제공한다.
 */
export function useDraftPersistence<T>({ draftKey, previewKey, buildPreview }: DraftPersistenceOptions<T>) {
  return useMemo(
    () => ({
      loadDraft(): LoadDraftResult<T> {
        const full = parseJson<T>(sessionStorage.getItem(draftKey));
        if (full) {
          return {
            value: full,
            usedPreview: false,
            warning: "",
          };
        }

        const preview = parseJson<T>(sessionStorage.getItem(previewKey));
        if (preview) {
          return {
            value: preview,
            usedPreview: true,
            warning: "sessionStorage 경량 저장본을 복원했습니다. 최신 설정을 동기화 중입니다.",
          };
        }

        return {
          value: null,
          usedPreview: false,
          warning: "",
        };
      },
      saveDraft(value: T): SaveDraftResult {
        const fullPayload = JSON.stringify(value);
        const previewPayload = JSON.stringify(buildPreview(value));
        try {
          sessionStorage.setItem(draftKey, fullPayload);
          sessionStorage.setItem(previewKey, previewPayload);
          return { partial: false, warning: "" };
        } catch {
          try {
            sessionStorage.setItem(previewKey, previewPayload);
            return {
              partial: true,
              warning: "sessionStorage 용량 제한으로 경량 캐시만 저장했습니다.",
            };
          } catch {
            return {
              partial: true,
              warning: "sessionStorage 저장에 실패했습니다.",
            };
          }
        }
      },
      clearDraft() {
        sessionStorage.removeItem(draftKey);
        sessionStorage.removeItem(previewKey);
      },
    }),
    [buildPreview, draftKey, previewKey],
  );
}
